import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';

import placeholder from '@/assets/images/Doctor_Placeholder@2x.png';
import * as customPropTypes from '@/custom-prop-types';
import { Button } from '@/design-system/atoms/Button';
import { Grid, GridColumn } from '@/design-system/atoms/Grid';
import { LazyIconArrowRightCircle } from '@/design-system/atoms/Icons/IconArrowRightCircle/Lazy';
import { LazyIconMapPin } from '@/design-system/atoms/Icons/IconMapPin/Lazy';
import { Image } from '@/design-system/atoms/Image';
import { StarRatings } from '@/design-system/atoms/StarRatings';
import { Text } from '@/design-system/atoms/Text';
import { summarizeArray } from '@/utils/summarizeArray';

import styles from './ProviderCarouselCard.module.scss';

const ProviderCarouselCard = ({
    title,
    link,
    image,
    acceptingNewPatients,
    primarySpecialty,
    specialties,
    location,
    averageRating,
    onlineBookingAvailable,
    availability,
    analytics,
    coveoAnalyticsCallbacks,
    ...props
}) => {
    const ImageComponent = image ? (
        <Image {...image} fallbackSrc={placeholder} alt={title} roundedCorners />
    ) : (
        <Image src={placeholder} alt="Doctor" roundedCorners />
    );

    const specialtiesText = summarizeArray(specialties, 3, primarySpecialty);
    const locationsText = location[0];

    const analyticsObj = useMemo(() => {
        return {
            ...analytics,
            providerName: title,
            providerSpecialties: specialtiesText,
            providerRating: averageRating,
        };
    }, [title, specialtiesText, averageRating, analytics]);

    const handleClick = useCallback(() => {
        coveoAnalyticsCallbacks?.onClick?.();
    }, [coveoAnalyticsCallbacks]);

    const handleContextMenu = useCallback(() => {
        coveoAnalyticsCallbacks?.onContextMenu?.();
    }, [coveoAnalyticsCallbacks]);

    const handleMouseDown = useCallback(() => {
        coveoAnalyticsCallbacks?.onMouseDown?.();
    }, [coveoAnalyticsCallbacks]);

    const handleMouseUp = useCallback(() => {
        coveoAnalyticsCallbacks?.onMouseUp?.();
    }, [coveoAnalyticsCallbacks]);

    const handleTouchStart = useCallback(() => {
        coveoAnalyticsCallbacks?.onTouchStart?.();
    }, [coveoAnalyticsCallbacks]);

    const handleTouchEnd = useCallback(() => {
        coveoAnalyticsCallbacks?.onTouchEnd?.();
    }, [coveoAnalyticsCallbacks]);

    useEffect(() => {
        coveoAnalyticsCallbacks?.cancelPendingSelect?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={styles[`provider-carousel-card`]} {...props}>
            <Grid
                col={{ sm: 4, md: 4, lg: 4 }}
                row={{ sm: 3, md: 3, lg: 3 }}
                className={styles[`provider-carousel-card__grid`]}
            >
                <GridColumn
                    colStart={{ sm: 1, md: 1, lg: 1 }}
                    colSpan={{ sm: 2, md: 2, lg: 2 }}
                    rowStart={{ sm: 1, md: 1, lg: 1 }}
                    rowSpan={{ sm: 1, med: 1, lg: 1 }}
                    className={styles[`provider-carousel-card__image`]}
                >
                    {ImageComponent}
                </GridColumn>
                <GridColumn
                    colStart={{ sm: 1, md: 1, lg: 1 }}
                    colSpan={{ sm: 4, md: 4, lg: 4 }}
                    rowStart={{ sm: 2, md: 2, lg: 2 }}
                    rowSpan={{ sm: 1, md: 1, lg: 1 }}
                    className={styles[`provider-carousel-card__content`]}
                >
                    {acceptingNewPatients && (
                        <div className={styles[`provider-carousel-card__eyebrow`]}>
                            <Text
                                content="Accepting New Patients"
                                tag={Text.TAG.P}
                                variant={Text.VARIANT.T4}
                                className={styles[`provider-carousel-card__eyebrow-text`]}
                            />
                        </div>
                    )}
                    <Button
                        label={title}
                        buttonStyle={Button.STYLE.TEXT_TITLE}
                        className={styles[`provider-carousel-card__title`]}
                        iconComponent={LazyIconArrowRightCircle}
                        {...link}
                        analytics={{
                            ...analyticsObj,
                            interactionType: 'cta',
                        }}
                        onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                        onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                        onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                        onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                        onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                        onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                    />
                    {specialtiesText && (
                        <Text
                            content={specialtiesText}
                            tag={Text.TAG.P}
                            variant={Text.VARIANT.T2}
                        />
                    )}
                    {(locationsText ||
                        (typeof averageRating === 'number' && averageRating > 0)) && (
                        <div className={styles[`provider-carousel-card__inline`]}>
                            {locationsText && (
                                <div className={styles[`provider-carousel-card__location`]}>
                                    <LazyIconMapPin />
                                    <Text
                                        content={locationsText}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T3}
                                    />
                                </div>
                            )}
                            {typeof averageRating === 'number' && averageRating > 0 && (
                                <div className={styles[`provider-carousel-card__rating`]}>
                                    <StarRatings total={5} ratings={averageRating} />
                                    <Text
                                        content={averageRating?.toFixed(1)}
                                        tag={Text.TAG.P}
                                        variant={Text.VARIANT.T3}
                                        className={styles[`provider-carousel-card__rating-text`]}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </GridColumn>
                <GridColumn
                    colStart={{ sm: 1, md: 1, lg: 1 }}
                    colSpan={{ sm: 4, md: 4, lg: 4 }}
                    rowStart={{ sm: 3, md: 3, lg: 3 }}
                    rowSpan={{ sm: 1, md: 1, lg: 1 }}
                    className={styles[`provider-carousel-card__footer`]}
                >
                    {onlineBookingAvailable && (
                        <Button
                            label={'View All Times & Schedule'}
                            buttonStyle={Button.STYLE.TEXT}
                            className={styles[`provider-carousel-card__cta`]}
                            {...availability.schedulingCta}
                            analytics={{
                                ...analyticsObj,
                                interactionType: 'cta',
                            }}
                            onClick={coveoAnalyticsCallbacks ? handleClick : undefined}
                            onContextMenu={coveoAnalyticsCallbacks ? handleContextMenu : undefined}
                            onMouseDown={coveoAnalyticsCallbacks ? handleMouseDown : undefined}
                            onMouseUp={coveoAnalyticsCallbacks ? handleMouseUp : undefined}
                            onTouchStart={coveoAnalyticsCallbacks ? handleTouchStart : undefined}
                            onTouchEnd={coveoAnalyticsCallbacks ? handleTouchEnd : undefined}
                        />
                    )}
                </GridColumn>
            </Grid>
        </div>
    );
};

ProviderCarouselCard.propTypes = {
    /**
     * Image of the provider
     */
    image: PropTypes.shape(Image.propTypes),
    /**
     * Whether the provider is accepting new patients
     */
    acceptingNewPatients: PropTypes.bool,
    /**
     * Name of the provider
     */
    title: PropTypes.string,
    /**
     * Link object
     */
    link: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    /**
     * primary specialty of the provider
     */
    primarySpecialty: PropTypes.string,
    /**
     * List of specialties of the provider
     */
    specialties: PropTypes.array,
    /**
     * Location of the provider
     */
    location: PropTypes.array,
    /**
     * Average rating of the provider
     */
    averageRating: PropTypes.number,
    /**
     * Whether provider has online scheduling
     */
    onlineBookingAvailable: PropTypes.bool,
    /**
     * avalaibility Link object
     */
    availabilityLink: PropTypes.shape({
        href: PropTypes.string,
        target: PropTypes.string,
        rel: PropTypes.string,
        csr: PropTypes.bool,
        ariaLabel: PropTypes.string,
    }),
    /**
     * analytics object
     */
    analytics: customPropTypes.analyticsPropType,
    /**
     * coveoAnalyticsCallbacks object
     */
    coveoAnalyticsCallbacks: PropTypes.shape({
        cancelPendingSelect: PropTypes.func,
        onClick: PropTypes.func,
        onContextMenu: PropTypes.func,
        onMouseDown: PropTypes.func,
        onMouseUp: PropTypes.func,
        onTouchStart: PropTypes.func,
        onTouchEnd: PropTypes.func,
    }),
};

export default ProviderCarouselCard;
